import * as Sentry from '@sentry/nextjs';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Address } from 'viem';
import { mainnet } from 'viem/chains';
import { ZodError } from 'zod';

import { ApiError } from '@/lib/errors/api';

import { getIntentsDeadlineSeconds } from '@/constants/chain';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { isRwaIssuer } from '@/utils/0x/token-registry.types';
import { getTokenPair } from '@/utils/amplitude';
import { zeroExApiFetcher } from '@/utils/fetcher';
import type { IntentsQuote } from '@/utils/intents/types';
import { isEthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';
import { getFeeRequestParams } from '@/utils/swap-fee-params';

import { liquiditySourcesEventHandlers } from '../store/liquiditySources';
import { useLiquiditySourcesStore } from '../store/liquiditySources';
import { useTokenResult } from './tokenQueries/useTokenResult';
import { useSwapFeeQuery } from './transactionModule/useSwapFeeQuery';
import { useMatchaWallets } from './useMatchaWallets';
import { useResolvedSlippage } from './useResolvedSlippage';
import { UseSwapQuoteParams } from './useSwapQuote';

export type IntentsQuoteQueryOptions = Omit<
  UseQueryOptions<IntentsQuote & { taker: Address }, ApiError | ZodError | Error>,
  'queryKey' | 'queryFn'
>;

export function useIntentsQuote(
  query: UseSwapQuoteParams | null,
  options: IntentsQuoteQueryOptions,
): UseQueryResult<IntentsQuote & { taker: Address }, ApiError | ZodError | Error> {
  Sentry.setTag('app.0x_api_version', 2);

  // get parameters
  const chainId = query?.sellTokenChainId ?? query?.buyTokenChainId ?? mainnet.id;
  const { ethereumWallet } = useMatchaWallets();
  const { isGasless = false, telemetry: { buyAmountDecimal = '', buyAmountUsd = 0 } = {} } =
    query ?? {};
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);
  const { swapFeeBps, swapFeeRecipient, swapFeeToken } = useSwapFeeQuery({
    buyTokenAddress:
      query && isEthereumAddress(query.buyTokenAddress) ? query.buyTokenAddress : undefined,
    buyTokenChainId: query?.buyTokenChainId,
    sellTokenAddress:
      query && isEthereumAddress(query.sellTokenAddress) ? query.sellTokenAddress : undefined,
    sellTokenChainId: query?.sellTokenChainId,
  });

  const { token: sellTokenResult } = useTokenResult(query?.sellTokenAddress, chainId);
  const { token: buyTokenResult } = useTokenResult(query?.buyTokenAddress, chainId);
  const sellTokenSymbol = sellTokenResult?.symbol;
  const buyTokenSymbol = buyTokenResult?.symbol;
  const hasRwaToken = isRwaIssuer(sellTokenResult?.issuer) || isRwaIssuer(buyTokenResult?.issuer);

  // Matcha Auto uses the same stored slippage mode as standard swaps.
  const {
    bps: resolvedSlippageBps,
    isAuto,
    isReady: isSlippageReady,
  } = useResolvedSlippage({
    chainId,
    sellToken: sellTokenResult,
    buyToken: buyTokenResult,
  });

  const excludedSources = useLiquiditySourcesStore(
    liquiditySourcesEventHandlers,
  ).getExcludedSources(chainId);

  // build URL
  const url = useMemo(() => {
    if (
      !isGasless ||
      !query?.sellAmount ||
      query.sellAmount === '0' ||
      !ethereumWallet ||
      !isEthereumAddress(query.sellTokenAddress) ||
      !isEthereumAddress(query.buyTokenAddress)
    ) {
      return null;
    }

    const params = new URLSearchParams({
      chainId: chainId.toString(),
      buyToken: query.buyTokenAddress,
      sellToken: query.sellTokenAddress,
      sellAmount: query.sellAmount,
      taker: ethereumWallet.address,
      // deadline will be added dynamically in the fetcher
      // [RWA] drop swap fees for RWA tokens
      ...(hasRwaToken ? {} : getFeeRequestParams({ swapFeeBps, swapFeeRecipient, swapFeeToken })),
      ...(excludedSources && { excludedSources }),
      ...(recipientAddress ? { recipient: recipientAddress } : {}),
      slippageBps: resolvedSlippageBps.toString(),
    });

    return routes.api.INTENTS_QUOTE + '?' + params.toString();
  }, [
    isGasless,
    query,
    chainId,
    swapFeeBps,
    swapFeeRecipient,
    swapFeeToken,
    hasRwaToken,
    ethereumWallet,
    excludedSources,
    recipientAddress,
    resolvedSlippageBps,
  ]);

  // build context for metrics & error reporting
  const context = useMemo(
    () => ({
      buy_token_address: query?.buyTokenAddress,
      buy_token_amount: buyAmountDecimal,
      buy_token_symbol: buyTokenSymbol,
      chain_id: chainId,
      order_type: 'market_auto',
      pair: query
        ? getTokenPair(
            chainId && sellTokenSymbol && query.sellTokenAddress
              ? { chainId, symbol: sellTokenSymbol, address: query.sellTokenAddress }
              : undefined,
            chainId && buyTokenSymbol && query.buyTokenAddress
              ? { chainId, symbol: buyTokenSymbol, address: query.buyTokenAddress }
              : undefined,
          )
        : undefined,
      sell_token_address: query?.sellTokenAddress,
      sell_token_amount: query?.sellAmount,
      sell_token_symbol: sellTokenSymbol,
      usd_dollar_amount: buyAmountUsd,
      wallet_connected_address: ethereumWallet?.address ?? null,
      swap_fee_bps: hasRwaToken ? undefined : swapFeeBps,
      swap_fee_recipient: hasRwaToken ? undefined : swapFeeRecipient,
      excluded_sources: excludedSources,
    }),
    [
      query,
      buyAmountDecimal,
      buyAmountUsd,
      buyTokenSymbol,
      chainId,
      sellTokenSymbol,
      ethereumWallet?.address,
      swapFeeBps,
      swapFeeRecipient,
      excludedSources,
      hasRwaToken,
    ],
  );

  // Custom fetcher that adds a fresh deadline to the URL.
  // Computing it inside a useMemo would make it stale (it would not be
  // recomputed when React Query revalidates), so we compute it per fetch.
  const fetcherWithFreshDeadline = (baseUrl: string) => {
    // Floored because the 0x API expects whole seconds.
    const deadline = Math.floor(Date.now() / 1000 + getIntentsDeadlineSeconds(chainId));
    const urlWithDeadline = baseUrl + `&deadline=${deadline}`;
    return zeroExApiFetcher(urlWithDeadline, context);
  };

  const { enabled: callerEnabled = true, ...restOptions } = options;
  return useQuery<IntentsQuote & { taker: Address }, ApiError | ZodError | Error>({
    queryKey: ['intents-quote', url, isAuto],
    queryFn: async () => withSentSlippage(await fetcherWithFreshDeadline(url!), url!, isAuto),
    refetchOnReconnect: false,
    retry: false,
    ...restOptions,
    // Computed last so it overrides any `enabled` in `restOptions`; we
    // AND-merge so the leaf still self-disables when the URL guard fails
    // even if the caller passes `enabled: true`. `isSlippageReady` holds the
    // quote until Auto resolves, so it is fetched once at the final tolerance.
    enabled: url !== null && callerEnabled && isSlippageReady,
  });
}
